import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import {
  TextField,
  List,
  ListItemButton,
  Collapse,
  Divider,
  Tooltip,
  Stack,
  Typography,
  Box,
  Link,
} from "@mui/material";
import copy from "copy-to-clipboard";
import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  formatXealthUrl,
  renderMeasureDeliveryStatusIcon,
} from "../../../utils";

import { DOCS, MEASURE_TYPES } from "../../../utils/constants";
import shouldFilterNullValues from "../../../utils/deviceFilterConfig";
import Drawer from "../../Drawer";
import { PAYLOAD_SCHEMA } from "../../Endpoint/EndpointFormUtils";

function MeasureDetail(props) {
  const { rowSelected, onClose } = props;
  const { validated_by: validatedBy } =
    rowSelected.clinical_validation_status || {};
  const [endpointSelected, setEndpointSelected] = useState(null);

  const handleCloseDrawer = () => {
    onClose();
  };

  const handleSelectEndpoint = (id) => () => {
    if (id === endpointSelected) return setEndpointSelected(null);
    return setEndpointSelected(id);
  };

  const hubInfo = [
    {
      title: "Hub ID",
      value: rowSelected.hub?.hub_id,
    },
    {
      title: "Transmitting Hub ID",
      value: rowSelected.transmitting_hub?.hub_id,
    },
    {
      title: <TitleWithInfoIcon title="Hub Time" url={DOCS.MEASURE_TIME.url} />,
      value: rowSelected.hub_time
        ? new Date(rowSelected.hub_time).toLocaleString()
        : "Not reported",
      tooltip:
        "The time reported by the hub. This may differ from the device time.",
    },
  ];

  const deviceInfo = [
    {
      title: "Make/Model",
      value: `${rowSelected.device.make || "N/A"} ${
        rowSelected.device.model_display_name || "N/A"
      }`,
    },
    {
      title: "MAC Address",
      value: rowSelected.device.mac_address,
    },
    {
      title: (
        <TitleWithInfoIcon title="Device Time" url={DOCS.MEASURE_TIME.url} />
      ),
      value: rowSelected.reported_device_time
        ? new Date(rowSelected.reported_device_time).toLocaleString()
        : "Not reported",
      tooltip:
        "The time reported by the device. This may differ from the hub time.",
    },
  ];

  const clinicalValidation = [
    {
      title: "Status",
      value: rowSelected.clinical_validation_status?.status,
    },
    {
      title: "Validated By",
      value:
        validatedBy?.first_name && validatedBy?.last_name
          ? `${validatedBy?.first_name} ${validatedBy?.last_name}`
          : `${validatedBy?.username || "-"}`,
    },
    {
      title: "Validated Date",
      value: new Date(
        rowSelected.clinical_validation_status?.validated_time
      ).toLocaleString(),
    },
  ];

  const getEndpointUrl = (endpoint) =>
    endpoint.payload_schema === PAYLOAD_SCHEMA.XEALTH_v1_0
      ? formatXealthUrl(endpoint.url)
      : endpoint.url;

  return (
    <Drawer
      open={!!rowSelected.id}
      width={450}
      anchor="right"
      onClose={handleCloseDrawer}
      title={
        <Stack direction="row">
          <Box
            sx={{
              alignItems: "center",
            }}
          >
            {MEASURE_TYPES[rowSelected.type]?.icon || null}
          </Box>
          <Stack>
            <Typography
              variant="h1"
              sx={{
                margin: 0,
                textTransform: "capitalize",
                fontSize: 20,
                display: "flex",
                alignItems: "center",
              }}
            >
              {MEASURE_TYPES[rowSelected.type]?.label || rowSelected.type}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                margin: 0,
                textTransform: "capitalize",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                color: "grey",
              }}
            >
              {new Date(rowSelected.timestamp).toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
      }
    >
      <Typography
        variant="h3"
        sx={{
          marginBottom: "5px",
          marginLeft: "24px",
          fontSize: "14px",
        }}
      >
        Measure
      </Typography>
      <BorderLeftContentBlock>
        {(shouldFilterNullValues(rowSelected.device)
          ? Object.entries(rowSelected.value).filter(
              ([, value]) =>
                value !== null && value !== undefined && value !== ""
            )
          : Object.entries(rowSelected.value)
        ).map(([field, fieldValue]) => {
          const measureTypeField =
            MEASURE_TYPES[rowSelected.type]?.fields[field.toLowerCase()];
          const name = measureTypeField?.label || field;
          const unit = measureTypeField?.unit || "";
          const value = `${fieldValue} ${unit}`;
          return <LineItem key={name} title={name} value={value} />;
        })}
      </BorderLeftContentBlock>
      <Typography
        variant="h3"
        sx={{
          marginBottom: "5px",
          marginLeft: "24px",
          fontSize: "14px",
        }}
      >
        Hub
      </Typography>
      <BorderLeftContentBlock>
        {hubInfo.map((data) => (
          <LineItem
            key={data.title}
            title={data.title}
            value={data.value}
            tooltip={data.tooltip}
          />
        ))}
      </BorderLeftContentBlock>
      <Typography
        variant="h3"
        sx={{
          marginBottom: "5px",
          marginLeft: "24px",
          fontSize: "14px",
        }}
      >
        Device
      </Typography>
      <BorderLeftContentBlock sx="device">
        {deviceInfo.map((data) => (
          <LineItem
            key={data.title}
            title={data.title}
            value={data.value}
            tooltip={data.tooltip}
          />
        ))}
      </BorderLeftContentBlock>
      {rowSelected.clinical_validation_status && (
        <>
          <Typography
            variant="h3"
            sx={{
              marginBottom: "5px",
              marginLeft: "24px",
              fontSize: "14px",
            }}
          >
            Clinical Validation
          </Typography>
          <BorderLeftContentBlock sx="device">
            {clinicalValidation.map((data) => (
              <LineItem
                key={data.title}
                title={data.title}
                value={data.value}
                tooltip={data.tooltip}
              />
            ))}
          </BorderLeftContentBlock>
        </>
      )}
      <List>
        {rowSelected.delivery?.map((item) => (
          <Box key={item.id}>
            <ListItemButton
              onClick={handleSelectEndpoint(item.id)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 14,
                ...(endpointSelected === item.id && {
                  boxShadow: "0px 1px 2px rgba(0,0,0,0.39)",
                }),
              }}
            >
              <Box>
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {renderMeasureDeliveryStatusIcon(item.status)}
                  <Box component="span" sx={{ marginLeft: "15px" }}>
                    {getEndpointUrl(item.endpoint)}
                  </Box>
                </Box>
              </Box>
              {endpointSelected ? (
                <ArrowDropUp
                  sx={{
                    "&:hover": {
                      background: "#ECECF3",
                    },
                  }}
                />
              ) : (
                <ArrowDropDown
                  sx={{
                    "&:hover": {
                      background: "#ECECF3",
                    },
                  }}
                />
              )}
            </ListItemButton>
            <Collapse
              in={endpointSelected === item.id}
              timeout="auto"
              unmountOnExit
            >
              <Box sx={{ marginTop: "15px" }}>
                <BorderLeftContentBlock sx="endpoint">
                  <LineItem
                    title="Request URL"
                    value={
                      <Box sx={{ overflowWrap: "anywhere" }}>
                        {getEndpointUrl(item.endpoint)}
                      </Box>
                    }
                  />
                  <LineItem
                    title="Status Code"
                    value={item.status_code || "No status"}
                  />
                </BorderLeftContentBlock>
                <PayloadTextArea
                  value={
                    item.response_payload
                      ? item.response_payload
                      : `Empty response body`
                  }
                  title="Response"
                  isItalic={!item.response_payload}
                />
                <PayloadTextArea
                  value={
                    item.request_payload
                      ? item.request_payload
                      : "Request in progress"
                  }
                  title="Request"
                  isItalic={!item.request_payload}
                />
              </Box>
            </Collapse>
            <Divider />
          </Box>
        ))}
      </List>
    </Drawer>
  );
}

function PayloadTextArea(props) {
  const { value, isItalic, title } = props;
  const [copied, setIsCopied] = useState(false);

  const onCopyContent = (content) => () => {
    copy(content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const formattedValue =
    typeof value === "object"
      ? JSON.stringify(value, undefined, 4)
      : value.replace("\r", "\n");

  return (
    <Box>
      <Stack flexDirection="row">
        <Typography
          sx={{ m: "10px 0 10px 24px", fontSize: "14px", fontWeight: "bold" }}
        >
          {title}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
          <ContentCopyIcon
            sx={{
              cursor: "pointer",
              fontSize: "18px",
              marginTop: "12px",
              marginLeft: "5px",
            }}
            onClick={onCopyContent(formattedValue)}
          />
        </Tooltip>
      </Stack>
      <Box>
        <TextField
          value={formattedValue}
          multiline
          disabled
          sx={{
            width: "100%",
            "& > .MuiInputBase-root > textarea": {
              fontFamily: "monospace",
            },
            "& .MuiInputBase-root": {
              background: "#ECECF3",
              borderRadius: 0,
              fontSize: 14,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#fff",
              border: 0,
            },
            "& .MuiInputBase-root-MuiOutlinedInput-root": {
              borderRadius: 0,
            },
            ...(isItalic && { fontStyle: "italic" }),
          }}
        />
      </Box>
    </Box>
  );
}

function BorderLeftContentBlock(props) {
  const { children } = props;
  return (
    <Box
      sx={{
        marginLeft: "40px",
        background: "transparent",
        borderLeft: "4px solid rgb(0 0 0 / 75%)",
      }}
    >
      <Box sx={{ paddingLeft: "20px" }}>{children}</Box>
    </Box>
  );
}

function LineItem(props) {
  const { title, value, tooltip } = props;
  const content = (
    <Stack
      flexDirection="row"
      alignItems="flex-start"
      sx={{ fontSize: "14px" }}
    >
      <Box sx={{ width: 220, minWidth: 150, fontWeight: "bold" }}>{title}</Box>
      {value}
    </Stack>
  );

  return tooltip ? (
    <Tooltip
      title={<Typography sx={{ fontSize: "14px" }}>{tooltip}</Typography>}
      arrow
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
}

function TitleWithInfoIcon(props) {
  const { title, url } = props;
  return (
    <Stack flexDirection="row" gap="3px">
      {title}
      <Link
        href={url}
        target="_blank"
        rel="noopener"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <InfoIcon sx={{ fontSize: 14 }} />
      </Link>
    </Stack>
  );
}

TitleWithInfoIcon.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

LineItem.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.element,
  ]),
  tooltip: PropTypes.string,
};

LineItem.defaultProps = {
  value: "",
  tooltip: null,
};

BorderLeftContentBlock.propTypes = {
  children: PropTypes.node.isRequired,
};

PayloadTextArea.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  title: PropTypes.string.isRequired,
  isItalic: PropTypes.bool,
};
PayloadTextArea.defaultProps = {
  isItalic: false,
};

MeasureDetail.propTypes = {
  rowSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MeasureDetail;
